/**
 * useLedgerDriftWatcher — Polling silencieux pour détecter les drifts comptables.
 *
 * - Actif uniquement pour les chefs d'agence
 * - Poll toutes les LEDGER_DRIFT_POLL_INTERVAL_MS (défaut 10 min)
 * - Affiche un toast.warning UNIQUEMENT si nb_suppliers_with_drift > 0
 * - Cooldown : ne re-notifie pas plus d'1 fois par 30 min pour le même état
 * - Stocke last_seen_drift_hash dans sessionStorage (par tab)
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supplierReceiptsAPI } from '../services/api';

const POLL_INTERVAL_MS = 10 * 60 * 1000;          // 10 minutes
const COOLDOWN_MS = 30 * 60 * 1000;                // 30 minutes
const STORAGE_KEY = 'ledger_drift_watcher_v1';

const _readState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch { return {}; }
};
const _writeState = (s) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
};

export default function useLedgerDriftWatcher(isChef) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isChef) return undefined;

    const checkOnce = async () => {
      try {
        const { data } = await supplierReceiptsAPI.getHealthDashboard();
        const driftCount = data?.nb_suppliers_with_drift || 0;
        const driftHash = `${driftCount}:${(data?.drift_suppliers || []).map((s) => s.supplier_id).sort().join('|')}`;

        const state = _readState();
        const now = Date.now();
        const recentlyNotified = state.last_drift_hash === driftHash && (now - (state.last_notified_at || 0) < COOLDOWN_MS);

        if (driftCount > 0 && !recentlyNotified) {
          toast.warning(
            `${driftCount} fournisseur${driftCount > 1 ? 's' : ''} en écart comptable détecté${driftCount > 1 ? 's' : ''}. Vérifier le dashboard.`,
            {
              duration: 10000,
              action: {
                label: 'Voir',
                onClick: () => { window.location.href = '/dashboard-chef'; },
              },
            },
          );
          _writeState({ last_drift_hash: driftHash, last_notified_at: now });
        }
        // Si drift revenu à 0 après alerte → toast.success de résolution (une fois)
        if (driftCount === 0 && state.last_drift_hash && !state.last_drift_hash.startsWith('0:')) {
          toast.success('Tous les fournisseurs sont à nouveau alignés.', { duration: 5000 });
          _writeState({ last_drift_hash: driftHash, last_notified_at: now });
        }
      } catch {
        // Silent — endpoint pas critique, on retentera au prochain tick
      }
    };

    // Premier check après 15 s (laisse le temps à la page de charger)
    const firstTimer = setTimeout(checkOnce, 15000);
    // Ensuite poll régulier
    timerRef.current = setInterval(checkOnce, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(firstTimer);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isChef]);
}
